import {controller, target, attr} from '@github/catalyst'
import {hasInteractions} from '@github-ui/has-interactions'
import {eventToHotkeyString} from '@github-ui/hotkey'
import {parseHTML} from '@github-ui/parse-html'

@controller('favorite-pins')
class FavoritePinsElement extends HTMLElement {
  @target declare form: HTMLFormElement
  @target declare limitNotice: HTMLElement
  @target declare input: HTMLInputElement
  @target declare list: HTMLElement
  @target declare submitButton: HTMLButtonElement
  @target declare loadMoreButton: HTMLButtonElement
  @target declare loadMoreSpinner: HTMLElement
  @target declare querySpinner: HTMLElement
  @target declare clearButton: HTMLButtonElement
  @target declare filterEmptyState: HTMLElement

  @attr datasource = ''
  @attr query = ''
  @attr page = 1

  declare private setTimeoutId: number
  private uncheckedItems: string[] = []

  get items() {
    return Array.from(this.list.children) as HTMLElement[]
  }

  async formModified(e: Event) {
    this.submitButton.disabled = !hasInteractions(this.form)

    // We need to keep track of what has been unchecked so that we can display the correct state
    // over multiple filter queries.
    const item = e.target as HTMLInputElement
    if (!item.checked && !this.uncheckedItems.includes(item.value)) {
      this.uncheckedItems.push(item.value)
    }
  }

  // Prevent more than n pinned repository selections.
  async limitPins() {
    const checkboxes = this.items.map(node => node.querySelector('input[type="checkbox"]') as HTMLInputElement)

    const used = checkboxes.filter(el => el.checked).length
    const limit = parseInt(this.getAttribute('max')!, 10)

    for (const el of checkboxes) {
      el.disabled = used === limit && !el.checked
    }

    const label = this.limitNotice.getAttribute('data-remaining-label') || ''
    const diff = limit - used
    this.limitNotice.textContent = `${used} ${label}`
    this.limitNotice.classList.toggle('color-fg-danger', diff < 1)
  }

  querySearchTerm() {
    this.withLoading(async () => {
      this.list.hidden = true
      this.page = 1
      const container = await this.fetchData(this.query, this.page)
      const selected = this.items.filter(
        node => (node.querySelector('input[type="checkbox"]') as HTMLInputElement)?.checked,
      )
      const items = Array.from(container.children) as HTMLElement[]

      // Items that have been unchecked prior to the current query will come back from the server checked.
      // We need to uncheck them before displaying the results.
      items.map(item => {
        const itemCheckbox = item.querySelector('input[type="checkbox"]') as HTMLInputElement
        const previouslyChecked = itemCheckbox.checked
        const currentlyUnchecked = this.uncheckedItems.includes(itemCheckbox.value)
        if (previouslyChecked && currentlyUnchecked) {
          itemCheckbox.checked = false
        }
      })

      const filteredSelectedItems = this.filterShownItems(selected, this.query)
      this.list.replaceChildren(...this.sortItems(filteredSelectedItems, items))
      this.filterEmptyState.hidden = this.list.children.length > 0
      this.limitPins()
      this.list.hidden = false

      return container
    })
  }

  filterShownItems(selected: HTMLElement[], query: string) {
    selected.map(item => {
      const name = item.textContent || ''
      item.hidden = !name.toLowerCase().includes(query.toLowerCase())
    })

    return selected
  }

  sortItems(pinned: HTMLElement[], unpinned: HTMLElement[]) {
    const pinnedIds = pinned.map(item => (item.querySelector('input[type="checkbox"]') as HTMLInputElement).value)

    const deduped = unpinned.filter(item => {
      return !pinnedIds.includes((item.querySelector('input[type="checkbox"]') as HTMLInputElement).value)
    })

    return pinned.concat(deduped)
  }

  queryNextPage() {
    this.withLoading(async () => {
      this.page += 1
      const container = await this.fetchData(this.query, this.page)
      if (container) {
        this.list.append(...container.children)
      }

      return container
    })
  }

  updateLoadMoreButton(container: HTMLElement) {
    const hasMore = container.getAttribute('data-has-more-items') === 'true'
    if (hasMore) {
      this.loadMoreButton.hidden = false
    } else {
      this.loadMoreButton.hidden = true
    }
  }

  async fetchData(query: string, page: number) {
    const url = new URL(this.datasource, window.location.origin)
    url.searchParams.append('q', query)
    url.searchParams.append('page', page.toString())

    const response = await fetch(url)
    const html = await response.text()
    const fragment = parseHTML(document, html)
    return fragment.firstChild as HTMLElement
  }

  async withLoading(fn: () => Promise<HTMLElement> | HTMLElement) {
    this.loadMoreButton.hidden = true
    this.loadMoreSpinner.hidden = false
    this.loadMoreSpinner.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'})
    const container = await fn()
    this.loadMoreSpinner.hidden = true
    this.updateLoadMoreButton(container)
  }

  handleSearchInput(evt: KeyboardEvent) {
    if (eventToHotkeyString(evt) === 'Escape') {
      evt.stopImmediatePropagation()
    }

    this.query = (evt.target as HTMLInputElement).value
    this.clearButton.hidden = this.query.length === 0

    clearTimeout(this.setTimeoutId)
    this.setTimeoutId = setTimeout(() => this.querySearchTerm(), 500) as unknown as number
  }

  handleClearSearch() {
    this.input.value = ''
    this.query = ''
    this.clearButton.hidden = true
    this.querySearchTerm()
  }
}
